import { Box, Btn, Txt } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { shortDate } from '../utils/format';

/** Skeleton block used while a region is loading. */
export function Skeleton({ className = '' }: { className?: string }): JSX.Element {
  return <Box className={`skeleton animate-shimmer rounded-lg ${className}`} aria-hidden />;
}

export function KpiSkeleton(): JSX.Element {
  return (
    <Box className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-5" aria-busy>
      <Box className="sm:col-span-2 xl:col-span-1 rounded-card bg-surface-card ring-1 ring-hairline shadow-card p-5">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-10 w-24 mt-4" />
        <Skeleton className="h-3 w-40 mt-3" />
      </Box>
      {[0, 1, 2, 3].map((i) => (
        <Box key={i} className="rounded-card bg-surface-card ring-1 ring-hairline shadow-card p-5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-16 mt-4" />
          <Skeleton className="h-3 w-28 mt-3" />
        </Box>
      ))}
    </Box>
  );
}

export function PanelSkeleton({ rows = 6 }: { rows?: number }): JSX.Element {
  return (
    <Box className="h-full rounded-card bg-surface-card ring-1 ring-hairline shadow-card p-5 sm:p-6" aria-busy>
      <Skeleton className="h-4 w-48" />
      <Box className="mt-6 flex flex-col gap-4">
        {Array.from({ length: rows }, (_, i) => (
          <Box key={i}>
            <Box className="flex justify-between">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-3 w-10" />
            </Box>
            <Skeleton className="h-2 w-full mt-2" />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function TableSkeleton({ rows = 8 }: { rows?: number }): JSX.Element {
  return (
    <Box className="p-4" aria-busy aria-label="Loading orders">
      {Array.from({ length: rows }, (_, i) => (
        <Box key={i} className="flex items-center gap-4 py-3 border-b border-hairline last:border-0">
          <Skeleton className="h-3.5 w-28 shrink-0" />
          <Skeleton className="h-3.5 w-40 shrink-0" />
          <Skeleton className="h-3.5 flex-1" />
          <Skeleton className="h-3.5 w-24 shrink-0" />
          <Skeleton className="h-3.5 w-20 shrink-0" />
        </Box>
      ))}
    </Box>
  );
}

/** Friendly empty state — the expected view for a date with no L48 orders. */
export function EmptyState({
  rangeText,
  warehouseLabel,
  filtered,
  onClearFilters,
  latestOrderDate,
  onJumpToLatest,
}: {
  /** Human label for the active range, e.g. "Today" or "1 Jan – 2 Mar 2024". */
  rangeText: string;
  warehouseLabel: string;
  filtered: boolean;
  onClearFilters?: () => void;
  /** Most recent date this warehouse has any order for, if known. */
  latestOrderDate?: string | null;
  /** Widens the range to cover all of the warehouse's data. */
  onJumpToLatest?: () => void;
}): JSX.Element {
  // Offering to widen only helps if the warehouse has data somewhere.
  const canJump = !filtered && !!latestOrderDate && !!onJumpToLatest;

  return (
    <Box className="flex flex-col items-center justify-center text-center px-6 py-16">
      <Box className="grid place-items-center w-12 h-12 rounded-2xl bg-surface-sunken ring-1 ring-hairline text-ink-muted">
        <Icon name="inbox" className="w-6 h-6" />
      </Box>
      <Txt as="p" className="mt-4 text-[15px] font-semibold text-ink-primary">
        {filtered ? 'No orders match these filters' : `No ${warehouseLabel} orders found for this range.`}
      </Txt>
      <Txt as="p" className="mt-1.5 text-[13px] text-ink-muted max-w-md">
        {filtered
          ? 'Try clearing the status filter or search term.'
          : `Nothing was received at ${warehouseLabel} during ${rangeText}.`}
      </Txt>

      {canJump ? (
        <>
          <Txt as="p" className="mt-1 text-[13px] text-ink-muted max-w-md">
            The most recent activity was on {shortDate(latestOrderDate!)}.
          </Txt>
          <Btn
            onPress={onJumpToLatest}
            className="mt-5 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] font-semibold bg-ink-primary text-surface-card hover:opacity-90 transition-opacity duration-150"
          >
            <Icon name="calendar" className="w-4 h-4" />
            Show all time
          </Btn>
        </>
      ) : null}

      {filtered && onClearFilters ? (
        <Btn
          onPress={onClearFilters}
          className="mt-5 px-3.5 py-2 rounded-lg text-[13px] font-medium bg-surface-card text-ink-primary ring-1 ring-hairline hover:bg-surface-sunken transition-colors duration-150"
        >
          Clear filters
        </Btn>
      ) : null}
    </Box>
  );
}

/**
 * Error state.
 *
 * Only the safe `{code, message}` pair from the API is shown — never a SQL
 * fragment, driver code, host name or stack trace.
 */
export function ErrorState({
  message,
  code,
  onRetry,
  compact = false,
}: {
  message: string;
  code?: string;
  onRetry?: () => void;
  compact?: boolean;
}): JSX.Element {
  return (
    <Box
      role="alert"
      className={[
        'flex flex-col items-center justify-center text-center rounded-card bg-surface-card ring-1 ring-hairline',
        compact ? 'px-5 py-8' : 'px-6 py-14',
      ].join(' ')}
    >
      <Box
        className="grid place-items-center w-11 h-11 rounded-2xl ring-1 ring-hairline"
        style={{ color: 'var(--life-returned)', backgroundColor: 'var(--surface-sunken)' }}
      >
        <Icon name="alert" className="w-5 h-5" strokeWidth={2} />
      </Box>
      <Txt as="p" className="mt-4 text-[15px] font-semibold text-ink-primary">
        Could not load this data
      </Txt>
      <Txt as="p" className="mt-1.5 text-[13px] text-ink-secondary max-w-md">
        {message}
      </Txt>
      {code ? (
        <Txt as="p" className="mt-2 text-[11px] text-ink-muted tabular">
          Reference: {code}
        </Txt>
      ) : null}
      {onRetry ? (
        <Btn
          onPress={onRetry}
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium bg-ink-primary text-surface-card hover:opacity-90 transition-opacity duration-150"
        >
          <Icon name="refresh" className="w-4 h-4" />
          Retry
        </Btn>
      ) : null}
    </Box>
  );
}
